import React from 'react';
import { Info, AlertCircle, Check, Mail, FileText, Square } from 'lucide-react';
import DashboardLayout from '../../components/dashboard/DashboardLayout';

const TopTenResumeMistakesChecklist = () => {
  return (
    <DashboardLayout>
      <div className="max-w-4xl mx-auto px-6 py-12 text-gray-800">
        {/* Header */}
        <h1 className="text-4xl font-bold mb-8">Top Ten Resume Mistakes Checklist</h1>

        {/* Info Box */}
        <div className="bg-gray-100 p-6 rounded-lg mb-12">
          <div className="flex items-start gap-3 mb-4">
            <Info className="w-5 h-5 mt-1" />
            <div>
              <h2 className="font-medium mb-2">Essential Overview</h2>
              <p className="text-gray-700">
                Avoid these common pitfalls to ensure your resume stands out for the right reasons. 
                Use this guide to fine-tune your resume and make a great first impression.
              </p>
            </div>
          </div>
        </div>

        {/* Mistakes List */}
        {[
          {
            title: "1. Overloading with Text",
            points: [
              "Avoid dense paragraphs or long bullet lists",
              "Use concise language and keep bullet points to 1-2 lines",
              "Maintain white space for readability"
            ]
          },
          {
            title: "2. Using an Objective Statement",
            points: [
              "Replace outdated objective statements with a professional summary",
              "Focus on your value and key achievements instead of what you're seeking"
            ]
          },
          {
            title: "3. Failing to Tailor Your Resume",
            points: [
              "Customize your resume for each job application",
              "Highlight relevant skills and experiences that align with the job description"
            ]
          },
          {
            title: "4. Lack of Keywords",
            points: [
              "Incorporate industry-specific keywords from the job posting",
              "Ensure your resume passes Applicant Tracking Systems (ATS)"
            ]
          },
          {
            title: "5. Including Irrelevant Information",
            points: [
              "Leave out personal details like age, marital status, and hobbies unrelated to the job",
              "Focus on professional achievements and relevant experiences"
            ]
          },
          {
            title: "6. Using Generic or Vague Language",
            points: [
              'Avoid phrases like "responsible for" or "worked on"',
              'Use action verbs and specific accomplishments (e.g., "Increased sales by 20%")'
            ]
          },
          {
            title: "7. Inconsistent Formatting",
            points: [
              "Ensure consistent font sizes, styles, and bullet point alignment",
              "Use a clean and professional layout"
            ]
          },
          {
            title: "8. Ignoring Quantifiable Results",
            points: [
              'Include metrics to demonstrate your impact (e.g., "Reduced costs by $10,000 annually")',
              "Avoid generic statements without supporting data"
            ]
          },
          {
            title: "9. Using Unprofessional Contact Information",
            points: [
              "Use a professional email address (e.g., firstname.lastname@gmail.com)",
              'Avoid novelty emails like "superstar123@aol.com"'
            ]
          },
          {
            title: "10. Submitting Without Proofreading",
            points: [
              "Check for spelling, grammar, and punctuation errors",
              "Have someone else review your resume for a fresh perspective"
            ]
          }
        ].map((mistake, index) => (
          <div key={index} className="bg-blue-50 p-6 rounded-lg mb-6">
            <div className="flex items-start gap-4">
              <AlertCircle className="w-5 h-5 text-red-600" />
              <div>
                <h3 className="font-semibold mb-4">{mistake.title}</h3>
                <ul className="space-y-3">
                  {mistake.points.map((point, i) => (
                    <li key={i} className="flex items-start gap-3">
                      <Square className="w-5 h-5 text-gray-400 flex-shrink-0 mt-1" />
                      <span>{point}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        ))}

        {/* Pro Tip Section */}
        <div className="bg-green-50 p-6 rounded-lg mb-6">
          <div className="flex items-start gap-4">
            <Check className="w-5 h-5 text-green-600" />
            <div>
              <h3 className="font-semibold mb-4">Pro Tip</h3>
              <p className="text-gray-700">
                Treat your resume as a marketing document. Its goal is to showcase your value and 
                secure an interview. Review this checklist before every submission to stay ahead 
                of the competition.
              </p>
            </div>
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
};

export default TopTenResumeMistakesChecklist;